<template>
  <section class="bg-gray50 pt-0">
    <!-- Search Bar -->
    <SearchListings
        ref="searchListings"
        :initial-search="searchValue"
        :initial-type="type"
        :initial-community="community"
        :window-size="windowSize"
        v-model="searchParams"
    />

    <!-- Controls Container -->
    <div
        class="flex w-full mx-auto px-40 mt-40 mb-65 md:my-40 justify-between items-center flex-wrap lg:flex-no-wrap"
    >
      <!-- "View" Type Buttons -->
      <div
          class="flex items-center justify-center lg:justify-start text-gray700 w-full lg:w-1/4 mb-25 lg:mb-0"
      >
        <!-- Split (not available on mobile) -->
        <button
            id="split"
            class="hidden xl:block py-3 bg-transparent outline-none ts-all hover:text-gray900 border-t-none border-b border-solid border-transparent hover:border-gray200"
            :class="{ 'border-gray400' : view == 'split'}"
            @click="setView('split')"
        >Split
        </button>

        <!-- Separator
          Note how it's hidden on mobile; the first option is, too,
          so we don't want a floating, useless separator to *start* the list)
        -->
        <div class="hidden xl:block w-3px h-3px mx-15 rounded-full bg-gray200"></div>

        <!-- List -->
        <button
            class="py-3 bg-transparent outline-none ts-all hover:text-gray900 border-t-none border-b border-solid border-transparent hover:border-gray200"
            :class="{ 'border-gray400' : view == 'list'}"
            @click="setView('list')"
        >List
        </button>

        <!-- Separator -->
        <div class="w-3px h-3px mx-15 rounded-full bg-gray200"></div>

        <!-- Map -->
        <button
            class="py-3 bg-transparent outline-none ts-all hover:text-gray900 border-t-none border-b border-solid border-transparent hover:border-gray200"
            :class="{ 'border-gray400' : view == 'map'}"
            @click="setView('map')"
        >Map
        </button>
      </div>

      <!-- "Sort" Controls -->
      <div
          class="flex items-center justify-center flex-wrap  lg:flex-no-wrap lg:justify-start w-full lg:w-auto mb-25 lg:mb-0"
      >
        <div
            class="text-xs font-secondary font-bold uppercase tracking-loose lg:mr-15 w-full lg:w-auto text-center"
        >
          Sort by:
        </div>

        <!-- Sort Options -->
        <vSelect :options="sortOptions" v-model="sortOption" :hideDefault="true"
                 class="mr-15 custom-select-borderless" data-list-sort/>

        <!-- Sort Direction -->
        <vSelect :options="sortDirections" v-model="sortDirection"
                 :hideDefault="true" class="custom-select-borderless"
        />

        <button
            v-if="hasActiveFilters"
            class="text-xs font-secondary font-bold uppercase tracking-loose text-red500 bg-transparent outline-none mt-15 lg:mt-0 lg:ml-20 w-full lg:w-auto"
            type="button"
            @click="resetFilters"
        >Reset Filters
        </button>
      </div>

      <!-- Matches Count -->
      <div class="w-full lg:w-1/4">
        <div class="text-center lg:text-right" v-if="matches">{{ matches }}
          Matches
        </div>
      </div>

    </div>

    <!-- Actual Listings -->
    <Listings
        :listings="listings"
        :total="matches"
        :orientation="orientation"
        v-infinite-scroll="infiniteLoad"
        infinite-scroll-distance="900"
        :class="{'opacity-50' : loading || loadingQueued}"
        class="trans"
        :view="view"
        :allLoaded="alreadyLoadedThemAll"
        :searchStringUpdated="searchStringUpdated"
        @update-map="mapBounding = $event"
        :query="(searchParams.includes('q='))"
        :page="page"
        :loading="loading"
        :limit="limit"
        :split-visible-count="splitVisibleCount"
        @get-listings-split="getListingsSplit()"
    />

    <!-- Loading Indicator -->
    <div v-if="loading"
         class="text-gray200 text-4xl font-bold py-100 flex items-center justify-center"
    >
      Loading, please wait...
    </div>

    <!-- No Listings Found -->
    <div
        v-if="!loading && !listings.length && view == 'list'"
        class="text-gray200 text-4xl font-bold py-100 flex items-center justify-center"
    >
      No listings found, please try another search.
    </div>

    <div v-if="loading && view == 'list'"
         class="w-full flex justify-center items-center py-80"
    >
      <spinner v-if="loading" :small="true"/>
    </div>

    <!-- Load More Button -->
    <div
        v-else-if="!loading && page % 3 == 0 && !alreadyLoadedThemAll && view == 'list'"
        class="w-full flex justify-center items-center py-80"
    >
      <a class="filled-button filled-button-red block cursor-pointer"
         @click="getListings">
        <span class="filled-button-content">Load More</span>
      </a>
    </div>

    <!-- No More Listing Found  -->
    <div v-if="listings.length && alreadyLoadedThemAll && view == 'list'"
         class="text-gray200 text-4xl font-bold py-100 flex items-center justify-center"
    >
      There are no more listings.
    </div>

  </section>
</template>

<script>
import _ from 'lodash';
import axios from 'axios';

import infiniteScroll from '../../directives/infinite-scroll';

import Listings from './listings.vue';
import SearchListings from './search-listings.vue';
import vSelect from '../general/v-select.vue';
import spinner from '../general/spinner.vue';
import addCommas from '../../helpers/addCommas';
import scrollToElement from '../../helpers/scrollToElement';

// Coordinates residential URL state, API pagination, sorting, and map/list views.
export default {
  props: {
    windowSize: { type: String, default: 'md' },
  },
  directives: {infiniteScroll},
  components: {
    Listings,
    SearchListings,
    vSelect,
    spinner
  },
  data() {
    return {
      listings: [],
      limit: 21,
      savedListingsData: null,
      showWWFirst: true,
      wwBrokers: 'brokers=70600&brokers=71581',
      currentSettings: '',
      wwListingsCount: 0,
      loading: true,
      loadingQueued: false,
      alreadyLoadedThemAll: false,
      matches: 0,
      page: 1,
      view: 'split',
      searchStringUpdated: false,
      searchValue: '',
      searchParams: 'type=residential&type=multifamily&type=land&minprice=1&',
      sortOption: this.view == 'list' ? 'ww' : 'showall',
      sortDirection: '-listdate',
      sortOptions: {
        'ww': 'Our Listings First',
        'showall': 'All Listings',
      },
      widths: {
        xs: 400,
        md: 576,
        md: 768,
        lg: 992,
        xl: 1200
      },
      windowWidth: 0,
      nextURL: '',
      type: '',
      community: '',
      hasCountiesNeighborhoodCity: false,
      mapBounding: null,
      mapBoundingVal: null, //holds mapBounding value so we are not constantly firing an event when mapBounding changes
      splitVisibleCount: 0,
      doubleLoad: false,
    };
  },
  watch: {
    searchParams() {
      this.loadingQueued = true
      this.alreadyLoadedThemAll = false
      this.queGetListings()
    },
    sortOption(e) {
      this.loadingQueued = true
      this.alreadyLoadedThemAll = false
      this.queGetListings()
    },
    sortDirection() {
      this.loadingQueued = true
      this.alreadyLoadedThemAll = false
      this.queGetListings()
    },
    mapBounding(e) {
      if (this.view != 'list') {

        //hold - we only want to update the value for mapBoundingVal
        if (e.hold) {
          this.mapBoundingVal = e.bounds;
        } else {
          this.mapBoundingVal = e
          this.loadingQueued = true
          this.alreadyLoadedThemAll = false
          this.queGetListings()
        }
      }
    },
    view() {
      if (this.view == 'list') {
        this.loadingQueued = true
        this.alreadyLoadedThemAll = false
        this.queGetListings()
      }

      this.setSortOption();
    }
  },
  updated() {
    if (this.windowSize != 'xl' && this.view != 'list') {
      this.view = 'list'
    }
  },
  methods: {
    setSortOption() {
      var $select = document.querySelector("[data-list-sort] select");

      try {
        if (this.view == 'list') {
          this.sortOption = 'ww';

          //find a better solution - enable select option
          $select.options[0].disabled = false;
        } else {
          //set sort option to 'showall' for map view
          this.sortOption = 'showall';

          //disable WW/Our listings first option
          $select.options[0].disabled = true;
        }
      } catch (err) {
        console.log("error: " + err.message);
      }
    },
    getHashParam(name) {
      var url = window.location.search;
      name = name.replace(/[\[\]]/g, '\\$&');
      var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
          results = regex.exec(url);
      if (!results) {
        return null;
      }
      if (!results[2]) {
        return '';
      }
      return decodeURIComponent(results[2].replace(/\+/g, ' '));
    },
    infiniteLoad() {
      if (this.page % 3 && this.nextURL && this.view == 'list') {
        this.getListings()
      } else if (this.page % 3 && this.view == 'split') {
        this.getListingsSplit();
      }
    },
    getListingsSplit(clear = false) {
      if (this.listings.length > 0) {
        //copied from OG code
        if (this.alreadyLoadedThemAll) {
          this.loading = false
        }
        ///
        if (!this.loading && !this.alreadyLoadedThemAll) {
          var index = clear ? 0 : this.splitVisibleCount

          this.loading = true

          //display listings starting from the index through to the listings at the limit+index
          this.splitVisibleCount = Math.min(this.limit + index, this.listings.length)
          this.loading = false
          this.page++;

          //check if we are at the end
          if (this.splitVisibleCount >= this.listings.length) {
            this.alreadyLoadedThemAll = true
          }
        }
      }
    },
    getListings({q, clear} = {}) {
      var vm = this
      var mapInView = (this.view != 'list' && this.mapBoundingVal)

      if (this.alreadyLoadedThemAll) {
        this.loading = false
      }
      if (!this.alreadyLoadedThemAll) {
        this.loading = true

        // If there's a next url and not clear
        if (this.nextURL && !clear) {
          var url = this.nextURL + "&";

          // If we're clearing the listings or starting fresh
        } else {

          // Build the URL with pagination and params
          var url = 'https://api.simplyrets.com/properties?count=true&';

          // Define a limit if we're not loading a map
          if (!mapInView) {

            // This allows scrolling back to your position, and keeping pagination right when returning from viewing an individual listing.
            //var limit = (Number(this.getHashParam('offset')) + Number(this.getHashParam('limit'))) || 21

            // This keeps the 500 limit from sticking when switching from a map view
            //limit = limit == 500 ? 21 : limit;

            var limit = this.limit;

            // Add the limit to the url
            url += 'limit=' + limit + '&';

            // If we're doing a map view, max out the limit
          } else {
            url += 'limit=500&';
          }

          // Add the search params from the search-listings component
          url = url + this.searchParams;

          // Add sort info
          url += 'sort=' + this.sortDirection + '&sortOption=' + this.sortOption + '&'

        }

        // Add the search to the qs
        if (this.searchValue) {
          //url += `q=${this.searchValue}`;
          //searchParams += `&q=${this.searchValue}`;
          this.searchValue = null
        }

        var currentUrl = url.replace('https://api.simplyrets.com/properties', '');

        //remove the default statuses : note this gets set in search-listings.vue
        //remove brokers id
        //remove offset
        currentUrl = currentUrl.replace('status=Active&status=Pending&status=ComingSoon&status=ActiveUnderContract&', '').replace(this.wwBrokers, '').replace(/(offset=).*?(&)/, '');


        // Update the hash
        window.history.replaceState(null, null, `${currentUrl}`);

        //history.pushState(null, null, `?${searchParams}`);
        //window.location.hash = url.replace('https://api.simplyrets.com/properties', '')

        //get query param
        var urlParams = new URLSearchParams(currentUrl);
        var query = urlParams.get('q');
        var wwonly = urlParams.get('wwonly');
        var counties = urlParams.get('counties')
        var neighborhoods = urlParams.get('neighborhoods');
        var cities = urlParams.get('cities');


        //////////////////////////////////////////////////////////////////////////////////////////
        // WW listings first fun
        //////////////////////////////////////////////////////////////////////////////////////////
        //Generic way for checking if the user has applied any filters
        // - if there is a change, set our savedListingsData back to null
        if (this.currentSettings.toString() != urlParams.toString()) {
          this.currentSettings = urlParams

          this.savedListingsData = null;
          this.wwListingsCount = 0;

          //because of the double query, this will get adjusted but we don't want to until we run the query first (super ugly, I'm sorry :) )
          if (!this.doubleLoad) {
            this.showWWFirst = true; //reset new results coming
          }
        }
        //////////////////////////////////////////////////////////////////////////////////////////


        //////////////////////////////////////////////////////////////////////////////////////////
        // Communities
        //////////////////////////////////////////////////////////////////////////////////////////
        if (counties || neighborhoods || cities) {
          this.hasCountiesNeighborhoodCity = true;
        } else {
          this.hasCountiesNeighborhoodCity = false;
        }


        //////////////////////////////////////////////////////////////////////////////////////////
        // Maps
        //////////////////////////////////////////////////////////////////////////////////////////
        // If the map is in view, search based on the points on the map
        if (mapInView && !query && !this.hasCountiesNeighborhoodCity) {
          this.showWWFirst = false; //WW listings don't show first in map view

          url += 'points=' + this.mapBoundingVal._ne.lat + ',' + this.mapBoundingVal._ne.lng + '&'
          url += 'points=' + this.mapBoundingVal._ne.lat + ',' + this.mapBoundingVal._sw.lng + '&'
          url += 'points=' + this.mapBoundingVal._sw.lat + ',' + this.mapBoundingVal._sw.lng + '&'
          url += 'points=' + this.mapBoundingVal._sw.lat + ',' + this.mapBoundingVal._ne.lng + '&'
        }
        //////////////////////////////////////////////////////////////////////////////////////////


        //check if user checked "Only West and Woodall Listings"
        // - add broker ids to url params
        if (wwonly) {
          url += this.wwBrokers;
        }

        //////////////////////////////////////////////////////////////////////////////////////////
        // Getting the listings from the api: the double query conditional
        //////////////////////////////////////////////////////////////////////////////////////////
        //Double query
        // fisrt query we want WW listings only
        // second query we want remaining listings with WW
        if (this.sortOption == 'ww' && this.showWWFirst && wwonly === null) {

          //set url with broker ids
          var urlWW = url.includes(this.wwBrokers) ? url : url + this.wwBrokers;

          //get WW listings
          this.getApiListings(urlWW, clear, query, false, true).then(() => {
            //now get remaining listings
            if (this.savedListingsData === null && this.showWWFirst) {
              this.doubleLoad = true; //not happy w/this :: prevent the reset when we add a new url (due to the double query)
              this.getApiListings(url, 'clearWW', query, true)
            }
          });
        }
        // sortOption != WW, business as usual
        else {
          this.wwListingsCount = 0;
          this.getApiListings(url, clear, query).then(() => {
            //split view mods
            if (this.view == 'split') {
              //this only gets called once (no load more w/api), make sure we reset the page
              //start at zero, gets incremented inside getListingsSplit()
              this.page = 0
              this.splitVisibleCount = 0

              // scrolling back to top of results
              $('html, body').animate({scrollTop: 0}, 1200);


              this.$nextTick(() => {
                //set 'alreadyLoadedThemAll' to false for now - load side listings first
                this.alreadyLoadedThemAll = false

                this.getListingsSplit(true);
              });
            }
          });


        }
        //////////////////////////////////////////////////////////////////////////////////////////
      }

    },
    getApiListings(url, clear, query, saveListings = false, wwListings = false) {
      var vm = this;
      var loadMore = true;

      return axios.get(url, {
        auth: {
          username: 'dev_gkd161z1',
          password: '7331f601341j9871'
        }
      }).then(response => {
        this.loading = false
        this.loadingQueued = false


        //set wwlistings count.
        //Mainly used to determine if we should load the 'showall' listings or not (this way we don't show 4 listings with "Matches" == +3k results)
        if (wwListings) {
          this.wwListingsCount = response.headers['x-total-count'];
        } else {
          //set 'matches'
          //-- keep in mind that our 'showall' count could be 0, if so lets use the wwListingsCount
          var count = parseInt(response.headers['x-total-count']) > 0 ? response.headers['x-total-count'] : this.wwListingsCount;

          if (count) {
            this.matches = addCommas(count.toString());
          } else {
            this.matches = 0;
          }
        }


        //////////////////////////////////////////////////////////////////////////////////////////
        // Store second query
        //////////////////////////////////////////////////////////////////////////////////////////
        //store the listings if we are not going to use them. Save a query.
        // - note: the saveListings were basically only used to get the total records/'matches' : had to get hacky due to lack of api options.
        // - we have this data might as well store it.
        if (saveListings === true) {
          this.savedListingsData = {
            'listings': response.data,
            'nextLink': this.getNextLink(response),
          }

          //check if we have enough WW listings to show
          // -- if not, lets load more and set the response to null so the data pulls from our saved object
          // -- yes, we could have done this a bit different but the logic was already built out, hackathon continues
          if (this.wwListingsCount <= this.limit) {
            response = null
          } else {
            loadMore = false;
          }
        }
        //////////////////////////////////////////////////////////////////////////////////////////


        //update listings
        if (loadMore) {
          var newListings = response ? response.data : [];
          var hasNext = this.getNextLink(response);


          //////////////////////////////////////////////////////////////////////////////////////////
          // Update Next URL
          //////////////////////////////////////////////////////////////////////////////////////////
          if (hasNext) {
            this.nextURL = hasNext
          } else if (this.savedListingsData !== null && this.savedListingsData.nextLink) {
            this.nextURL = this.savedListingsData.nextLink;

            this.alreadyLoadedThemAll = false;
          } else {
            this.alreadyLoadedThemAll = true;
          }
          //////////////////////////////////////////////////////////////////////////////////////////


          //////////////////////////////////////////////////////////////////////////////////////////
          // Update Listings
          //////////////////////////////////////////////////////////////////////////////////////////
          /*
          * if we made it to this checkpoint it means we don't have anymore WW listings
          */
          if (this.savedListingsData !== null && !hasNext) {
            var spacer = this.wwListingsCount > 0 ? [{"isSpacer": true}] : [];
            var temp = spacer.concat(this.savedListingsData.listings)

            //update listings
            newListings = this.savedListingsData.listings.length > 0 ? newListings.concat(temp) : [];

            //viewed all of WW listings, no longer need special query
            this.showWWFirst = false;

            //no longer need this
            this.savedListingsData = null;
          }
          //////////////////////////////////////////////////////////////////////////////////////////


          //////////////////////////////////////////////////////////////////////////////////////////
          // Notify Listings Change
          //////////////////////////////////////////////////////////////////////////////////////////
          if (clear == 'clearWW') {
            this.listings = this.listings.concat(newListings)
          } else if (clear) {
            this.page = 1
            this.listings = newListings;

          } else {
            this.page++
            this.listings = this.listings.concat(newListings)
          }
          /***************************************************************************************/


          if (window.location.search.includes('mlsid')) {
            var mlsId = window.location.search.split('mlsid=')[1]
            vm.$nextTick(function() {
              scrollToElement('[id^="listing-' + mlsId + '-"]', -150)
            })
          }

          vm.searchStringUpdated = false
          //notify the map that we found a query string and the listings are ready
          //note: 'query' doesn't do much but it allows us to notify a change in value (ths value doesn't get used beyond that)
          if (query) {
            vm.searchStringUpdated = 'updated-' + query; //append query to let the map know there was a change in value (creating event)
          } else if (this.hasCountiesNeighborhoodCity) {
            const urlParams = new URLSearchParams(window.location.search);
            const query = urlParams.get('counties') + urlParams.get('neighborhoods');

            vm.searchStringUpdated = 'updated-' + query; //append query - signals a change in value and calls the proper method: This is all its used for (trigger change)
          }
        }

      });
    },
    getNextLink(response) {
      if (response && response.headers['link']) {
        var headerLink = response.headers['link'].replace('<', '').split('>;');

        //get 'rel' value from headers[link]
        //this contains a value of 'next' or 'prev'
        var paginationURL = headerLink[1] ? headerLink[1].split('"')[1] : false;

        //check for 'next' value
        if (paginationURL === 'next') {
          return headerLink[0];
        }
      }

      return false;

    },
    setView(view) {
      this.view = view;
    },
    resetFilters() {
      if (this.$refs.searchListings) {
        this.$refs.searchListings.resetFilters();
      }
    },
    queGetListings: _.debounce(
        function() {
          this.getListings({clear: true})
        },
        // This is the number of milliseconds we wait for the user to stop typing.
        1250
    ),
  },
  computed: {
    orientation() {
      if (this.windowSize == 'xs' || this.view != 'list') {
        return 'vertical'
      } else {
        return 'horizontal'
      }
    },
    sortDirections() {
      var sortDirections = {
        '-listprice': "Highest Price",
        'listprice': "Lowest Price",
        '-listdate': "Newest Listings",
        'listdate': "Oldest Listings",
        '-baths': "Highest Baths",
        'baths': "Lowest Baths",
        '-beds': "Highest Beds",
        'beds': "Lowest Bed",
      }

      return sortDirections
    },
    hasActiveFilters() {
      const params = new URLSearchParams(this.searchParams);
      const activeKeys = [
        'q',
        'maxprice',
        'minbeds',
        'maxbeds',
        'minbaths',
        'maxbaths',
        'minarea',
        'maxarea',
        'minyear',
        'water',
        'features',
        'agent',
        'wwonly',
        'community',
        'counties',
        'neighborhoods',
        'cities',
        'subtype',
      ];

      if (activeKeys.some(key => params.has(key))) {
        return true;
      }

      const minPrice = Number(params.get('minprice') || 10000);
      if (minPrice !== 10000) {
        return true;
      }

      const defaultStatuses = ['Active', 'Pending', 'ComingSoon', 'ActiveUnderContract'];
      const statuses = params.getAll('status');
      if (statuses.length && !_.isEqual(statuses.sort(), defaultStatuses.sort())) {
        return true;
      }

      const defaultTypes = ['residential', 'multifamily', 'land'];
      const types = params.getAll('type');
      return types.length > 0 && !_.isEqual(types.sort(), defaultTypes.sort());
    }
  },
  mounted() {
    const urlParams = new URLSearchParams(window.location.search);

    const query = urlParams.get('q');
    if (query) {
      this.searchValue = query;
    }

    const community = urlParams.get('community');
    if (community) {
      this.community = community;
      this.hasCountiesNeighborhoodCity = true;
    }

    /* check for the actual filtered/api params */
    /* main purpose of this is to get this to trigger a re-zoom (similar to search) that works with the original code */
    const county = urlParams.get('counties');
    const neighborhood = urlParams.get('neighborhoods');
    const cities = urlParams.get('cities');
    if (county || neighborhood || cities) {
      this.hasCountiesNeighborhoodCity = true; //we have a county or neighborhood selected
    }

    const type = urlParams.get('type');
    if (type) {
      this.type = type;
    }

    const sortDirection = urlParams.get('sort');
    if (sortDirection) {
      this.sortDirection = sortDirection
    }

    const sortOption = urlParams.get('sortOption');
    if (sortOption) {
      this.sortOption = sortOption
    }

    this.setSortOption();
  }
};
</script>
